import type { ReactNode } from 'react';
import { makeStyles } from '@material-ui/core/styles';

/*
 * Skin for the code-insights Releases card, on the mock's "Recent deployments"
 * grammar — a list of rows inside one card rather than the vendor's stacked
 * `ListItem`s.
 *
 * The mock's deployment row, measured from its own inline styles:
 *
 *   display:flex; align-items:center; gap:12px; height:44px; padding:0 22px;
 *   border-bottom:1px solid #f7f7f6
 *     · 7px status dot
 *     · version, DM Mono 12px #202020
 *     · optional status pill, 11px/500, radius 999, padding 2px 8px
 *     · spacer
 *     · relative time, DM Mono 11px #9a9a9a
 *
 * Two rows of that spec cannot be filled here. The vendor renders no timestamp,
 * so there is no relative time and none is invented — the right-hand slot stays
 * empty. And it renders no status, so the release's own tag glyph takes the
 * leading mark's place instead of a coloured dot.
 *
 * A skin rather than a rebuild because the card's data hooks are not exported
 * from the package, only the assembled component. Scoped through a
 * `display: contents` wrapper because the vendor's `makeStyles` is UNNAMED and
 * emits a bare `makeStyles-releaseTitle-<n>` any component could also produce.
 */

/* The mock's row separator. Lighter than the header rule (#f0f0ef); no token. */
const ROW_HAIRLINE = 'var(--hone-portal-hairline-row)';

const useStyles = makeStyles({
  root: {
    display: 'contents',

    /*
     * Rows run to the card's edge, so the body padding moves onto them. The
     * global card rule sets that padding with `!important`, so this has to
     * match it — the wrapper class is what wins the specificity tie.
     */
    '& [class*="MuiCard-root"] > [class*="MuiCardContent-root"]': {
      padding: '0 !important',
    },
    '& [class*="MuiList-root"]': {
      padding: 0,
    },

    '& [class*="MuiListItem-root"]': {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      height: 44,
      padding: '0 22px',
      borderBottom: `1px solid ${ROW_HAIRLINE}`,
    },

    /*
     * The row's anchor holds the release name, the tag glyph and the tag name,
     * in that order. The glyph is pulled to the front as the row's leading mark.
     */
    '& [class*="MuiListItem-root"] a': {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      flex: 1,
      minWidth: 0,
      fontFamily: 'var(--bui-font-monospace)',
      fontSize: 12,
      color: 'var(--bui-fg-primary)',
      textDecoration: 'none',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },

    /*
     * The release NAME is hidden and the tag left as the row's version. GitHub
     * defaults a release's name to its tag, so rendering both gives every row
     * "v2.0.0    v2.0.0"; and the name is nullable where the tag never is, so
     * the tag is the field that always resolves to a version.
     */
    '& [class*="makeStyles-releaseTitle"]': {
      display: 'none',
    },

    '& [class*="makeStyles-releaseTagIcon"]': {
      order: -1,
      flex: '0 0 auto',
      fontSize: 13,
      color: 'var(--hone-portal-fg-muted)',
    },

    /* Pre-release marker: the badge grammar, not MUI's filled primary pill. */
    '& [class*="MuiChip-root"]': {
      height: 18,
      borderRadius: 999,
      background: 'var(--hone-portal-fill-inset)',
      color: 'var(--hone-portal-fg-body)',
    },
    '& [class*="MuiChip-label"]': {
      padding: '0 8px',
      fontSize: 10.5,
      fontWeight: 500,
    },
  },
});

export function ReleaseRows({ children }: { children: ReactNode }) {
  const classes = useStyles();
  return <div className={classes.root}>{children}</div>;
}
